import { UserPlusIcon } from 'lucide-react'
import { useCallback, useContext, useState } from 'react'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import { CollapsibleDataContainer } from 'components/reusable/CollapsibleDataContainer'
import { Banner, TextLink } from 'slab'
import { type BrokersCoveredParticipant } from 'utils/web_api.schemas'

import CoveredParticipantSheet from './CoveredParticipantSheet'
import CoveredParticipantsTable, {
  COVERED_PARTICIPANTS_TABLE_FEATURES,
} from './CoveredParticipantsTable'

export const COVERED_PARTICIPANTS_VIEW_FEATURES = {
  ADD_DEPENDENT: 'covered_participants_add_dependent',
  EDIT_PARTICIPANT: COVERED_PARTICIPANTS_TABLE_FEATURES.EDIT_PARTICIPANT,
} as const

export type CoveredParticipantsViewViewFeature = typeof COVERED_PARTICIPANTS_VIEW_FEATURES[
  keyof typeof COVERED_PARTICIPANTS_VIEW_FEATURES
]

interface CoveredParticipantsViewProps {
  hasEmployerICHRAConfig: boolean
  memberId: string
}

const Heading = ({
  isAddingDependentsEnabled,
  onAddClick,
}: {
  isAddingDependentsEnabled?: boolean
  onAddClick: () => void
}) => (
  <div className='flex items-center justify-between'>
    <div className='flex items-center space-x-2'>
      <span className='heading-md'>Dependents</span>
      {isAddingDependentsEnabled && (
        <div className='cursor-pointer' onClick={(e) => e.stopPropagation()}>
          <TextLink color='purple' leftIcon={UserPlusIcon} onClick={onAddClick}>
            Add Dependent
          </TextLink>
        </div>
      )}
    </div>
  </div>
)

const CoveredParticipantsView = ({
  hasEmployerICHRAConfig,
  memberId,
}: CoveredParticipantsViewProps) => {
  const { enabledFeatures, planYear } = useContext(BrokersMemberContext)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [selectedParticipant, setSelectedParticipant] = useState<any>(undefined)
  const isAddingDependentsEnabled = hasEmployerICHRAConfig &&
    enabledFeatures?.includes(COVERED_PARTICIPANTS_VIEW_FEATURES.ADD_DEPENDENT)

  const handleAddClick = useCallback(() => {
    setSelectedParticipant(undefined)
    setIsSheetOpen(true)
  }, [])

  const handleEditClick = useCallback((participant: BrokersCoveredParticipant) => {
    setSelectedParticipant(participant)
    setIsSheetOpen(true)
  }, [])

  const successContent = (
    <div className='p-2'>
      <CoveredParticipantsTable
        memberId={memberId}
        planYear={planYear}
        onEdit={handleEditClick}
      />
      {planYear && (
        <CoveredParticipantSheet
          isOpen={isSheetOpen}
          setIsOpen={setIsSheetOpen}
          memberId={memberId}
          planYear={planYear}
          participant={selectedParticipant}
        />
      )}
    </div>
  )

  const errorContent = (
    <Banner title='No ICHRA Config found. Dependents cannot be viewed or edited without an ICHRA Config.' />
  )

  return (
    <CollapsibleDataContainer
      heading={
        <Heading
          isAddingDependentsEnabled={isAddingDependentsEnabled}
          onAddClick={handleAddClick}
        />
      }
      isLoading={false}
      error={!hasEmployerICHRAConfig}
      localStorageKey='covered-participants-view'
      loadingContent={<div>Loading dependents...</div>}
      errorContent={errorContent}
      successContent={successContent}
    />
  )
}

export default CoveredParticipantsView
